"use client";
import { useCallback, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/shadcn/select";
import useFilters from "@/app/lib/features/filters/filters.hook";
import {
  GeneralFilterInput,
  GeneralFilterList,
} from "@/app/lib/features/filters/filters.types";
import GeneralFilterPriceSection from "./GeneralFilterPriceSection";
import GeneralFilterListSection from "./GeneralFilterListSection";
import ProductFilterSystemSkeleton from "../../elements/skeletons/ProductFilterSystemSkeleton";

interface ProductFilterSystemProps {
  disableMinimumFilter?: boolean;
}

const ProductFilterSystem = ({ disableMinimumFilter }: ProductFilterSystemProps) => {
  const {
    sortOptions,
    generalFilterItems,
    selected,
    updateSort,
    isGeneralLoading,
    generalStatus,
  } = useFilters();

  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);

  const updateGradients = useCallback((swiper: SwiperType) => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  }, []);

  
  // Skeleton yalnızca veri hiç yokken göster — filtre değişiminde mevcut liste kalır
  if ((generalStatus === "idle" || isGeneralLoading) && generalFilterItems.length === 0) {
    return <ProductFilterSystemSkeleton />;
  }

  const filterNodes = generalFilterItems.map((filter) =>
    filter.type === "input" ? (
      <GeneralFilterPriceSection
        key={filter.key}
        filter={filter as GeneralFilterInput}
      />
    ) : (
      <GeneralFilterListSection
        key={filter.key}
        filter={filter as GeneralFilterList}
        disableMinimumFilter={disableMinimumFilter}
      />
    ),
  );

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="hidden md:flex items-center gap-2 flex-wrap">
          {filterNodes}
        </div>

        <div className="block md:hidden relative">
          <Swiper
            spaceBetween={8}
            freeMode={{
              enabled: true,
              momentumRatio: 0.25,
              momentumVelocityRatio: 0.25,
            }}
            onSwiper={updateGradients}
            onSlideChange={updateGradients}
            onProgress={updateGradients}
            onTouchEnd={(s) => setTimeout(() => updateGradients(s), 50)}
            onTransitionEnd={updateGradients}
            modules={[FreeMode]}
            slidesPerView="auto"
          >
            {generalFilterItems.map((filter) => (
              <SwiperSlide key={filter.key} className="w-auto!">
                {filter.type === "input" ? (
                  <GeneralFilterPriceSection
                    filter={filter as GeneralFilterInput}
                  />
                ) : (
                  <GeneralFilterListSection
                    filter={filter as GeneralFilterList}
                  />
                )}
              </SwiperSlide>
            ))}
            <div
              className={`absolute top-0 left-0 z-20 w-5 sm:w-8 h-full bg-linear-to-r from-white to-transparent transition-opacity duration-300 pointer-events-none ${showLeftGradient ? "opacity-100" : "opacity-0"}`}
            />
            <div
              className={`absolute top-0 right-0 z-20 w-5 sm:w-8 h-full bg-linear-to-l from-white to-transparent transition-opacity duration-300 pointer-events-none ${showRightGradient ? "opacity-100" : "opacity-0"}`}
            />
          </Swiper>
        </div>
      </div>

      {sortOptions.length > 0 && (
        <div className="shrink-0">
          <Select value={selected.sort} onValueChange={updateSort}>
            <SelectTrigger className="w-auto min-w-28 max-w-44 h-9">
              <SelectValue placeholder="Sıralama" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((opt) => (
                <SelectItem key={opt.key} value={opt.key}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default ProductFilterSystem;
